/*
  Component: Mobile menu drawer
  Responsibility: which tab is showing, and which groups inside it are open. Nothing else —
                  the drawer's own open and close belongs to assets/kl-header.js, which owns
                  the is-menu-open class on the header root and has done since before this
                  file existed.

  The markup renders in its finished state: the first tab expanded, the rest hidden, every
  group open. So there is nothing to establish on load and no flash of a different layout
  before this deferred script runs — the same first-paint reasoning as the header. A visitor
  whose browser never runs this gets the first tab's menu, fully readable, which is why
  assets/kl-mobile-menu-no-js.css only has to put the other tabs' panels back.

  No layout value is set anywhere below. Tabs and panels move through the `hidden` property
  and aria-expanded; everything visual is assets/kl-mobile-menu.css reacting to those.

  Prefixed constants: every authored script shares one global scope, so a bare TAB_SELECTOR
  here would collide with the same name in another kl-*.js and kill whichever loads second.
*/

const KL_MOBILE_MENU_TAB_SELECTOR = '[data-kl-mobile-menu-tab]';
const KL_MOBILE_MENU_PANEL_SELECTOR = '[data-kl-mobile-menu-panel]';
const KL_MOBILE_MENU_BANNER_PANEL_SELECTOR = '[data-kl-mobile-menu-banner-panel]';
const KL_MOBILE_MENU_VIEW_ALL_SELECTOR = '[data-kl-mobile-menu-view-all-panel]';
const KL_MOBILE_MENU_VIEW_ALL_BAR_SELECTOR = '.kl-mobile-menu__view-all-bar';
const KL_MOBILE_MENU_GROUP_SELECTOR = '[data-kl-mobile-menu-group]';
const KL_MOBILE_MENU_CAROUSEL_OPEN_SELECTOR = '[data-kl-mobile-menu-carousel-open]';
const KL_MOBILE_MENU_CAROUSEL_CLOSE_SELECTOR = '[data-kl-mobile-menu-carousel-close]';

/*
  Pure: given the control that was activated and the full list, says what each one's
  expanded state should become. Kept DOM-free so the "one at a time" rule can be read and
  tested without a browser.
*/
function getExpandedStates(controls, activeControl) {
  return controls.map((control) => control === activeControl);
}

function setDisclosure(control, panel, isExpanded) {
  control.setAttribute('aria-expanded', String(isExpanded));

  if (panel) panel.hidden = !isExpanded;
}

function setBannerDisclosure(drawerElement, panelId, isExpanded) {
  const bannerPanel = drawerElement.querySelector(
    KL_MOBILE_MENU_BANNER_PANEL_SELECTOR + '[data-kl-mobile-menu-banner-panel="' + panelId + '"]'
  );

  if (bannerPanel) bannerPanel.hidden = !isExpanded;
}

function setViewAllDisclosure(drawerElement, panelId, isExpanded) {
  const viewAllLink = drawerElement.querySelector(
    KL_MOBILE_MENU_VIEW_ALL_SELECTOR + '[data-kl-mobile-menu-view-all-panel="' + panelId + '"]'
  );

  if (viewAllLink) viewAllLink.hidden = !isExpanded;

  if (isExpanded) {
    const viewAllBar = drawerElement.querySelector(KL_MOBILE_MENU_VIEW_ALL_BAR_SELECTOR);
    if (viewAllBar) viewAllBar.hidden = !viewAllLink;
  }
}

function resetMobileMenuCarouselViews(drawerElement, restoreDetail = null) {
  drawerElement.querySelectorAll('[data-kl-mobile-menu-carousel-detail]').forEach((detail) => {
    detail.hidden = true;

    const bannerPanel = detail.closest('[data-kl-mobile-menu-banner-panel]');
    const panelId = detail.getAttribute('data-kl-mobile-menu-carousel-panel');
    const menuPanel = panelId ? drawerElement.querySelector('#' + CSS.escape(panelId)) : null;

    if (!restoreDetail && bannerPanel) {
      [...bannerPanel.children].forEach((child) => {
        if (child !== detail) child.hidden = false;
      });
    }

    if (restoreDetail === detail) {
      if (bannerPanel) {
        [...bannerPanel.children].forEach((child) => {
          if (child !== detail) child.hidden = false;
        });
      }
      if (menuPanel) menuPanel.hidden = false;
    }
  });
}

function initMobileMenuTabs(drawerElement) {
  const tabs = [...drawerElement.querySelectorAll(KL_MOBILE_MENU_TAB_SELECTOR)];

  if (!tabs.length) return;

  const firstPanelId = tabs[0].getAttribute('aria-controls');
  if (firstPanelId) setViewAllDisclosure(drawerElement, firstPanelId, true);

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const states = getExpandedStates(tabs, tab);

      resetMobileMenuCarouselViews(drawerElement);

      tabs.forEach((candidate, index) => {
        const panelId = candidate.getAttribute('aria-controls');
        const panel = panelId ? drawerElement.querySelector('#' + CSS.escape(panelId)) : null;

        setDisclosure(candidate, panel, states[index]);
        if (panelId) setBannerDisclosure(drawerElement, panelId, states[index]);
        if (panelId) setViewAllDisclosure(drawerElement, panelId, states[index]);
      });
    });
  });
}

function initMobileMenuGroups(drawerElement) {
  const groups = [...drawerElement.querySelectorAll(KL_MOBILE_MENU_GROUP_SELECTOR)];

  if (!groups.length) return;

  groups.forEach((group) => {
    group.addEventListener('click', () => {
      /*
        Independent of every other group: the reference opens all of a tab's groups when
        that tab is chosen and lets a visitor close the ones they are not reading, so
        closing one must not disturb the rest.
      */
      const isExpanded = group.getAttribute('aria-expanded') === 'true';
      const bodyId = group.getAttribute('aria-controls');
      const body = bodyId ? drawerElement.querySelector('#' + CSS.escape(bodyId)) : null;

      setDisclosure(group, body, !isExpanded);
    });
  });
}

function initMobileMenuCarousels(drawerElement) {
  drawerElement.querySelectorAll(KL_MOBILE_MENU_CAROUSEL_OPEN_SELECTOR).forEach((openButton) => {
    openButton.addEventListener('click', () => {
      const carouselId = openButton.getAttribute('data-kl-mobile-menu-carousel-open');
      const panelId = openButton.getAttribute('data-kl-mobile-menu-carousel-panel');
      const bannerPanel = openButton.closest('[data-kl-mobile-menu-banner-panel]');
      const panel = drawerElement.querySelector('#' + CSS.escape(panelId));
      const detail = drawerElement.querySelector(
        '[data-kl-mobile-menu-carousel-detail="' + CSS.escape(carouselId) + '"]'
      );

      if (!bannerPanel || !panel || !detail) return;

      [...bannerPanel.children].forEach((child) => {
        if (child !== detail) child.hidden = true;
      });
      panel.hidden = true;
      detail.hidden = false;
      detail.querySelector(KL_MOBILE_MENU_CAROUSEL_CLOSE_SELECTOR)?.focus();
    });
  });

  drawerElement.querySelectorAll(KL_MOBILE_MENU_CAROUSEL_CLOSE_SELECTOR).forEach((closeButton) => {
    closeButton.addEventListener('click', () => {
      const detail = closeButton.closest('[data-kl-mobile-menu-carousel-detail]');
      const carouselId = detail?.getAttribute('data-kl-mobile-menu-carousel-detail');

      resetMobileMenuCarouselViews(drawerElement, detail);

      if (carouselId) {
        drawerElement.querySelector(
          KL_MOBILE_MENU_CAROUSEL_OPEN_SELECTOR + '[data-kl-mobile-menu-carousel-open="' + CSS.escape(carouselId) + '"]'
        )?.focus();
      }
    });
  });
}

function initMobileMenu(drawerElement) {
  try {
    initMobileMenuTabs(drawerElement);
    initMobileMenuGroups(drawerElement);
    initMobileMenuCarousels(drawerElement);
  } catch (error) {
    /*
      A drawer that fails to wire up still shows the first tab and every group in it,
      because that is the state the markup renders in. Logged rather than swallowed so the
      failure is findable.
    */
    console.debug('Mobile menu could not be initialised', { error });
  }
}

document.querySelectorAll('[data-kl-mobile-menu]').forEach(initMobileMenu);

// Theme editor: a re-rendered section arrives with fresh, uninitialised markup.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-kl-mobile-menu]').forEach(initMobileMenu);
});

/*
  Panels close the drawer as they navigate. A tap on a link inside it is a page change, and
  without this the drawer is still open behind the new page for the moment before it paints
  — visible on a slow connection. The header owns the class, so this asks for the close the
  same way the close button does rather than reaching for is-menu-open itself.
*/
document.addEventListener('click', (event) => {
  const drawerLink = event.target.closest('[data-kl-mobile-menu] a[href]');

  if (!drawerLink) return;
  if (drawerLink.hasAttribute('data-kl-search-open')) return;

  const closeButton = drawerLink
    .closest('[data-kl-mobile-menu]')
    .querySelector('[data-kl-mobile-menu-close]');

  if (closeButton) closeButton.click();
});
